/**
 * Product Details Component
 * 
 * Main component for displaying product details.
 */

'use client';

import { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { ProductEntity, PricingTier } from '@/domain/entities/product.entity';
import { ProductActions } from './ProductActions';
import { ProductPrice } from './ProductPrice';
import { ProductReview } from './ProductReview';
import { PackSelector } from './PackSelector';
import { ProductGallery, useTheme } from '@template-project/afrodita-components';
import { ProductBadge } from '@/components/atoms/Badge';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { useVolumeTiers } from '@/lib/hooks/use-volume-tiers';
import { usePricingContext } from '@/lib/hooks/use-pricing-context';
import type { ProductPricing } from '@template-project/storefront-bff-client';
import { storefrontLayoutLogger } from '@/lib/logger';

const Container = styled.div`
  display: grid;
  grid-template-columns: 52fr 48fr;
  gap: ${({ theme }) => theme.space.xl};
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space.lg} ${theme.space.md}`};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.space.lg};
    padding: ${({ theme }) => `${theme.space.md} ${theme.space.sm}`};
  }
`;

const GalleryColumn = styled.div`
  position: sticky;
  top: ${({ theme }) => theme.space.md};
  height: fit-content;
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    position: relative;
    top: 0;
  }
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  padding: 0;
  
  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: 0 ${({ theme }) => theme.space.sm};
  }
`;

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  align-items: flex-start;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  line-height: 1.3;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    font-size: 22px;
    line-height: 1.4;
  }
`;

const PolicyBanner = styled.div`
  margin-top: ${({ theme }) => theme.space.lg};
  padding: ${({ theme }) => theme.space.md};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const PolicyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const PolicyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const PolicyTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PolicyText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  line-height: 1.5;
  padding-left: 28px;

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  }
`;

const SeeInYourSizeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.medium};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.tertiary || theme.colors.background.secondary};
    opacity: 0.8;
  }
`;

export interface ProductDetailsProps {
  product: ProductEntity;
  pricing?: ProductPricing | null;
  selectedVariantId?: string;
  onVariantChange?: (variantId: string) => void;
  onAddToCart?: (variantId: string, quantity: number) => Promise<void>;
}

export function ProductDetails({
  product,
  pricing,
  selectedVariantId,
  onVariantChange,
  onAddToCart,
}: ProductDetailsProps) {
  const theme = useTheme();
  const { t } = useTranslation('pdp');
  const { countryConfig } = useIntl();
  const { currency: pricingCurrency, country: pricingCountry } = usePricingContext();

  // Find selected variant or default to first enabled variant
  const selectedVariant =
    product.variants.find(v => v.id === selectedVariantId && v.enabled) ||
    product.variants.find(v => v.enabled) ||
    product.variants[0];

  // Fetch volume tiers from storefront-bff for the current product + variant + currency.
  // Returns an empty array when there are no tiers or pricing is unavailable.
  const { data: volumeTiers = [] } = useVolumeTiers({
    productId: product.uuid,
    variantId: selectedVariant?.id,
    currencyCode: pricingCurrency,
    countryCode: pricingCountry,
  });

  // Only show the PackSelector when the backend returned more than one tier.
  const hasPackSelector = volumeTiers.length > 1;

  // selectedTier tracks which tier the user has selected in the PackSelector.
  // When the user changes tiers, we update the displayed price and the quantity
  // that ProductActions will pass to onAddToCart.
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);

  // Reset tier selection whenever the variant changes so the price display
  // reflects the new variant's catalog price instead of the stale tier price.
  useEffect(() => {
    setSelectedTier(null);
  }, [selectedVariant?.id]);

  // If tiers load later, default to the "Most Popular" or first tier.
  const effectiveTier = useMemo(() => {
    if (!hasPackSelector) return null;
    if (selectedTier) return selectedTier;
    return volumeTiers.find((t) => t.label) ?? volumeTiers[0] ?? null;
  }, [hasPackSelector, selectedTier, volumeTiers]);

  // When a tier is active, its unit_price overrides the variant's catalog price
  // in the ProductPrice display, and its min_quantity is the add-to-cart qty.
  const priceOverride = effectiveTier?.unit_price;
  // Use ?? not || so that a tier with compare_at_price: null suppresses the
  // catalog strikethrough price (don't fall through to MSRP on null).
  const compareAtOverride = effectiveTier
    ? (effectiveTier.compare_at_price ?? undefined)
    : (selectedVariant?.compare_at_price ?? product.compare_at_price ?? undefined);
  const packQuantity = effectiveTier?.min_quantity ?? 1;

  // Prepare images and videos for gallery, interleaved by position
  const imageItems = product.images.map((img, idx) => ({
    key: img.id,
    url: img.src,
    alt: img.alt || product.title,
    position: img.position ?? idx + 1,
    zoomUrl: img.zoomUrl,
  }));

  const videoItems = (product.videos || []).map(vid => ({
    key: vid.id,
    url: vid.src,
    alt: vid.alt || product.title,
    isVideo: true as const,
    posterUrl: vid.posterUrl,
    mimeType: vid.mimeType,
    position: vid.position,
  }));

  const galleryImages = [...imageItems, ...videoItems]
    .sort((a, b) => a.position - b.position);

  // Determine badges based on product tags
  const showBestSellerBadge = product.tags?.includes('best-seller') || product.tags?.includes('bestseller');
  const showSaleBadge = product.compare_at_price && product.compare_at_price.display > product.price.display;
  const showBlackFridayBadge = product.tags?.includes('black-friday');
  const showSellingFastBadge = product.tags?.includes('selling-fast');

  // Render badges for gallery overlay
  const renderGalleryBadges = () => {
    const hasBadges = showSaleBadge || showBestSellerBadge || showBlackFridayBadge || showSellingFastBadge;
    if (!hasBadges) return undefined;
    
    return (
      <BadgeContainer>
        {showSaleBadge && <ProductBadge variant="sale" text="Sale" />}
        {showBlackFridayBadge && <ProductBadge variant="black-friday" text="Black Friday" />}
        {showSellingFastBadge && <ProductBadge variant="selling-fast" text="Selling Fast" />}
        {showBestSellerBadge && <ProductBadge variant="best-seller" text="Best Seller" />}
      </BadgeContainer>
    );
  };

  return (
    <Container>
      {/* Left Column - Product Gallery */}
      <GalleryColumn>
        <ProductGallery 
          images={galleryImages} 
          topTag={renderGalleryBadges()}
          zoomConfig={{ enabled: true, hideHint: true, zoomScale: 0.8 }}
        />
        {/* See In Your Size Button (if applicable) */}
        {product.tags?.includes('has-size-guide') && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <SeeInYourSizeButton onClick={() => storefrontLayoutLogger.info('Size guide requested from ProductDetails', {
              productId: product.uuid,
            })}>
              See In Your Size
            </SeeInYourSizeButton>
          </div>
        )}
      </GalleryColumn>

      {/* Right Column - Product Info & Actions */}
      <DetailsColumn>
        {/* Title & Rating Section */}
        <TitleSection>
          <Title>{product.title}</Title>
          <ProductReview 
            productId={product.uuid} 
            fallbackRating={product.rating?.average}
            fallbackCount={product.rating?.count}
          />
        </TitleSection>

        {/* Product Price -- tier override kicks in when PackSelector is active */}
        <ProductPrice
          price={priceOverride || selectedVariant?.price || product.price}
          compareAtPrice={compareAtOverride}
          discounts={pricing?.discounts}
        />

        {/* Volume discount tier selector (WoW Effect) */}
        {hasPackSelector && (
          <PackSelector
            tiers={volumeTiers}
            selectedMinQuantity={effectiveTier?.min_quantity ?? 1}
            onTierChange={setSelectedTier}
          />
        )}

        {/* Product Actions (qty controls hidden when PackSelector is active) */}
        <ProductActions
          product={product}
          selectedVariant={selectedVariant}
          variants={product.variants}
          options={product.options}
          onVariantChange={onVariantChange}
          onAddToCart={onAddToCart}
          hasPackSelector={hasPackSelector}
          packQuantity={packQuantity}
        />

        {/* Return Policy Banner */}
        <PolicyBanner>
          <PolicyHeader>
            <PolicyIconWrapper>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.text.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 11 12 14 22 4"></polyline>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            </PolicyIconWrapper>
            <PolicyTitle>{t('riskFree.title')}</PolicyTitle>
          </PolicyHeader>
          <PolicyText dangerouslySetInnerHTML={{ __html: t('riskFree.description') }} />
        </PolicyBanner>
      </DetailsColumn>
    </Container>
  );
}
